import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

// Public Turnstile site key (safe to expose in the browser).
export const TURNSTILE_SITE_KEY = "0x4AAAAAAD_Av95dyj3up4VQ";

// Tokens are single-use: parents call ref.current.reset() after every submit attempt that keeps the page open.
export const Turnstile = forwardRef(function Turnstile({ onVerify, action }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      onVerify("");
      if (widgetIdRef.current !== null && window.turnstile) window.turnstile.reset(widgetIdRef.current);
    },
  }), [onVerify]);

  useEffect(() => {
    let cancelled = false;
    let interval = null;

    const render = () => {
      if (cancelled || !containerRef.current || !window.turnstile || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action,
        callback: (token) => onVerify(token),
        "expired-callback": () => onVerify(""),
        "error-callback": () => onVerify(""),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (widgetIdRef.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* noop */ }
        widgetIdRef.current = null;
      }
    };
  }, [onVerify, action]);

  return <div ref={containerRef} data-action={action} data-testid="turnstile-widget" className="my-1" />;
});
